import React from "react"
import Button from "@material-ui/core/Button"
import Typography from "@material-ui/core/Typography"
import Paper from "@material-ui/core/Paper"
import AcceptedInvitationCard from "../../invitations/components/AcceptedInvitationCard"
import PendingInvitationCard from "../../invitations/components/PendingInvitationCard"
import { Link, useHistory } from "react-router-dom"
import { useLocation } from "react-router-dom"
import { paths } from "../../paths"
import { useAppDispatch } from "../../store"
import { FetchUser } from "../../users/store/actions"
import { useUserInvitations } from "../../invitations/hooks/use-user-invitations"
import { CircularProgress, Theme, makeStyles } from "@material-ui/core"
import { IInvitationResponse } from "../../invitations/interfaces/invitation-response"
import { useAuthUser } from "../../users/hooks/use-auth-user"
import { useI18n } from "../../hooks"
import { StringMap } from "../../types/common"
import { ArrowRight } from "react-feather"
import { useUrlWithContext } from "../../hooks/use-url-with-context"
import StarbrixLogo from "../../assets/icons/StarbrixLogo"

const UserInvitedRoute = () => {
  const classes = useStyles()
  const location = useLocation()
  const translations = useTranslations()
  const history = useHistory()
  const { from } = (location.state || {}) as { from?: Location }
  const isFromAccountVerification = from?.pathname === paths.accountVerification()
  const { pendingInvitations } = useUserInvitations()
  const dispatch = useAppDispatch()
  const [status, setStatus] = React.useState<Status>("loading")
  const [error, setError] = React.useState<string | null>(null)
  const isLoading = status === "loading"
  const isRejected = status === "rejected"
  const [acceptedInvitation, setAcceptedInvitation] = React.useState<IInvitationResponse | null>(null)
  const { user } = useAuthUser()
  const { createPathWithGivenContext } = useUrlWithContext()

  React.useEffect(() => {
    async function loader() {
      if (isFromAccountVerification) {
        try {
          await dispatch(FetchUser())
          setStatus("fulfilled")
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Unknown error"
          setStatus("rejected")
          setError(errorMessage)
        }
      }
    }
    loader()
  }, [isFromAccountVerification, dispatch])

  React.useEffect(() => {
    if (acceptedInvitation) {
      const invitedMemberWelcomePath = createPathWithGivenContext({
        path: paths.membershipWelcome(),
        mainContextId: acceptedInvitation.orgId,
        subContextId: acceptedInvitation.id,
      })
      history.push(invitedMemberWelcomePath)
    }
  }, [acceptedInvitation, createPathWithGivenContext, history])

  if (isRejected) return <p>{error}</p>
  if (!user || (isLoading && isFromAccountVerification)) return <CircularProgress />

  return (
    <Paper className={classes.container} variant="outlined" elevation={0}>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: 48,
        }}
      >
        <StarbrixLogo style={{ height: 48 }} />
      </div>
      <header className={classes.header}>
        <Typography variant="h5" gutterBottom>
          {user.firstname}, {translations.invitationReceived}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {translations.invitationReceivedDescription}
        </Typography>
      </header>
      <ul className={classes.invitationList}>
        {acceptedInvitation && <AcceptedInvitationCard invitation={acceptedInvitation} />}
        {pendingInvitations.map((invitation) => (
          <PendingInvitationCard
            key={invitation.id}
            invitation={invitation}
            setAcceptedInvitation={setAcceptedInvitation}
          />
        ))}
      </ul>
      <section style={{ marginTop: 20 }}>
        <Button
          component={Link}
          color="primary"
          to={createPathWithGivenContext({ path: paths.home(), mainContextId: user.id })}
          endIcon={<ArrowRight size={16} />}
          replace
        >
          {translations.skipButtonLabel}
        </Button>
      </section>
    </Paper>
  )
}

export default UserInvitedRoute

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    maxWidth: 800,
    margin: "50px auto",
    padding: theme.spacing(4),
  },
  invitationList: {
    padding: 0,
  },
  header: {
    margin: theme.spacing(4, 0, 3, 0),
  },
  logout: {
    marginTop: theme.spacing(3),
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.common.white,
  },
  card: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}))

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { translations: t } = useI18n("translation")
  const translations = (t || {}) as StringMap

  return {
    skipButtonLabel: translate("skipButtonLabel"),

    invitationReceivedDescription: translate("invitationReceivedDescription"),
    invitationReceived: translate("invitationReceived"),
  }

  function translate(key: keyof Translations) {
    return translations[key] || defaults[key]
  }
}

const defaultTranslations = {
  skipButtonLabel: "You can also skip this and go to your home page",
  invitationReceivedDescription:
    "You have received invitation from an organisation and you will be able to collaborate on the projects, tasks and other activities of the organisation when you accept the invitation.",
  invitationReceived: "Your colleagues are waiting . . .",
}

type Status = "loading" | "rejected" | "fulfilled"
type Translations = typeof defaultTranslations
